import React, { useEffect, useState, useCallback, useRef } from "react";
import { useApp } from "../context/AppContext";
import api from "../api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Sparkles, Upload } from "lucide-react";
import { InvoiceTable, InvoiceForm } from "./Sales";

export default function Purchases() {
  const { t, entity, currentEntity } = useApp();
  const [invoices, setInvoices] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [extracting, setExtracting] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [storage, setStorage] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get(`/invoices?entity_id=${entity}&kind=purchase`).then((r) => setInvoices(r.data)).catch(() => {});
    api.get(`/contacts?entity_id=${entity}`).then((r) => setContacts(r.data.filter((c) => c.type === "supplier"))).catch(() => {});
  }, [entity]);

  useEffect(() => { load(); }, [load]);

  const handleFile = async (file) => {
    if (!file) return;
    setExtracting(true);
    setExtracted(null);
    try {
      const fd = new FormData();
      fd.append("entity_id", entity);
      fd.append("file", file);
      const res = await api.post("/invoices/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setStorage({ storage_path: res.data.storage_path, original_filename: res.data.original_filename });
      const ex = res.data.extracted || {};
      if (ex.error) {
        toast.error(t("msg.extractFail"));
        setExtracted({});
      } else {
        setExtracted(ex);
      }
    } catch {
      toast.error(t("msg.error"));
    } finally {
      setExtracting(false);
    }
  };

  const set = (k, v) => setExtracted((e) => ({ ...e, [k]: v }));

  const register = async () => {
    try {
      await api.post("/invoices", {
        entity_id: entity, kind: "purchase",
        counterparty: extracted.vendor_name || "Fornitore",
        date: extracted.invoice_date || new Date().toISOString().slice(0, 10),
        due_date: extracted.due_date || null,
        description: extracted.description || "",
        net: parseFloat(extracted.net_amount) || 0,
        vat_rate: parseFloat(extracted.net_amount) > 0
          ? Math.round((parseFloat(extracted.vat_amount) / parseFloat(extracted.net_amount)) * 10000) / 100
          : 0,
        currency: extracted.currency || currentEntity?.currency || "EUR",
        storage_path: storage?.storage_path, original_filename: storage?.original_filename,
      });
      toast.success(t("msg.saved"));
      setExtracted(null);
      setStorage(null);
      load();
    } catch {
      toast.error(t("msg.error"));
    }
  };

  return (
    <div className="space-y-6" data-testid="purchases-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{t("nav.purchases")}</h1>
        <InvoiceForm kind="purchase" entityId={entity} onSaved={load} contacts={contacts} />
      </div>

      <Card data-testid="ai-extractor-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" />{t("purch.aiTitle")}</CardTitle>
          <p className="text-sm text-muted-foreground">{t("purch.aiDesc")}</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div
            data-testid="ai-pdf-dropzone"
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
            className={`border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors duration-200 ${
              dragOver ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}
          >
            <Upload className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
            <div className="text-sm text-muted-foreground">{t("purch.dropzone")}</div>
            <input ref={fileRef} type="file" accept="application/pdf" className="hidden"
              data-testid="ai-pdf-file-input" onChange={(e) => handleFile(e.target.files[0])} />
          </div>

          {extracting && (
            <div className="flex items-center gap-3 text-sm text-primary" data-testid="ai-extracting-indicator">
              <div className="h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              {t("purch.extracting")}
            </div>
          )}

          {extracted && !extracting && (
            <div className="space-y-4 border border-border rounded-lg p-4" data-testid="ai-extracted-review">
              <div className="text-sm font-semibold">{t("purch.extracted")}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div><Label>{t("inv.supplier")}</Label>
                  <Input value={extracted.vendor_name || ""} onChange={(e) => set("vendor_name", e.target.value)} data-testid="extracted-vendor-input" /></div>
                <div><Label>P.IVA / VAT</Label>
                  <Input value={extracted.vendor_vat || ""} onChange={(e) => set("vendor_vat", e.target.value)} data-testid="extracted-vat-input" /></div>
                <div><Label>{t("common.number")}</Label>
                  <Input value={extracted.invoice_number || ""} onChange={(e) => set("invoice_number", e.target.value)} data-testid="extracted-number-input" /></div>
                <div><Label>{t("common.date")}</Label>
                  <Input type="date" value={extracted.invoice_date || ""} onChange={(e) => set("invoice_date", e.target.value)} data-testid="extracted-date-input" /></div>
                <div><Label>{t("common.dueDate")}</Label>
                  <Input type="date" value={extracted.due_date || ""} onChange={(e) => set("due_date", e.target.value)} data-testid="extracted-due-input" /></div>
                <div><Label>{t("common.description")}</Label>
                  <Input value={extracted.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="extracted-description-input" /></div>
                <div><Label>{t("common.net")}</Label>
                  <Input type="number" step="0.01" value={extracted.net_amount ?? ""} onChange={(e) => set("net_amount", e.target.value)} data-testid="extracted-net-input" /></div>
                <div><Label>{t("common.vat")}</Label>
                  <Input type="number" step="0.01" value={extracted.vat_amount ?? ""} onChange={(e) => set("vat_amount", e.target.value)} data-testid="extracted-vatamount-input" /></div>
                <div><Label>{t("common.total")}</Label>
                  <Input type="number" step="0.01" value={extracted.total_amount ?? ""} onChange={(e) => set("total_amount", e.target.value)} data-testid="extracted-total-input" /></div>
              </div>
              <Button onClick={register} data-testid="ai-pdf-extract-submit">{t("purch.register")}</Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card data-testid="purchases-table-card">
        <CardContent className="p-0">
          {invoices.length === 0
            ? <div className="p-8 text-center text-sm text-muted-foreground">{t("common.noData")}</div>
            : <InvoiceTable invoices={invoices} kind="purchase" onRefresh={load} entityId={entity} />}
        </CardContent>
      </Card>
    </div>
  );
}
